import { alpha, styled } from '@mui/material';
import MUITypography from '@mui/material/Typography';
import type { ProductListResult } from '@partful/shared-types';
import { Job } from '@providers/job/types';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Highlight } from 'src/components/Product/Desktop/Panels/PartsCatalogue/Autocomplete/Highlight';
import { productCategoryPathEnumerationSeparator } from 'src/utils/urls';
import { getJobsWithRedirectURLs } from '../../../product-list/jobFiltering';

interface ProductSearchOptionProps {
  option: ProductListResult | Job;
  searchFieldState: string;
}

const productCategoryPathEnumerationSeparatorRegex = new RegExp(`\\${productCategoryPathEnumerationSeparator}`, 'g');

export default function ProductSearchOption({ option, searchFieldState }: Readonly<ProductSearchOptionProps>) {
  const highlightRegex = new RegExp(`${searchFieldState}`, 'i');

  if (typeof option.status === 'string') {
    const { productName, runID } = option;
    const displayedOptionText = `${productName} (In Progress)`;
    const jobWithRedirectURL = getJobsWithRedirectURLs([option])[0];

    /* istanbul ignore next */
    if (jobWithRedirectURL.redirectURL === '/product-library?error=CouldntMapProduct') {
      return null;
    }

    return (
      <JobOption
        title={displayedOptionText}
        productId={runID}
        highlightRegex={highlightRegex}
        redirectURL={jobWithRedirectURL.redirectURL}
      />
    );
  }

  /* istanbul ignore next - need to fix the test */
  if (typeof option.status !== 'string') {
    const { name, productId, productCategories } = option;
    const categoriesDisplayName = productCategories
      ?.map((pathEnumeration) => pathEnumeration.replace(productCategoryPathEnumerationSeparatorRegex, ' > '))
      .join(', ');

    return (
      <ConfigurationOption
        title={name}
        productId={productId}
        highlightRegex={highlightRegex}
        categoriesDisplayName={categoriesDisplayName}
        redirectURL={`/product-library/${productId}`}
      />
    );
  }

  /* istanbul ignore next */
  return null;
}

type OptionProps = {
  title: string;
  productId: string;
  highlightRegex: RegExp;
  redirectURL: string;
  // eslint-disable-next-line react/no-unused-prop-types
  categoriesDisplayName?: string;
};

/* istanbul ignore next - need to fix the test */
function ConfigurationOption({
  title,
  productId,
  highlightRegex,
  categoriesDisplayName,
  redirectURL,
}: Readonly<OptionProps>) {
  const { t: i18n } = useTranslation('productLibrary');

  return (
    <StyledProductSearchOption data-testid={`link-to-${productId}`} to={redirectURL}>
      <ProductSearchOptionTitle>
        <Highlight text={title} regex={highlightRegex} />
      </ProductSearchOptionTitle>
      {categoriesDisplayName && (
        <ProductSearchOptionSubTitle data-testid="categories-breadcrumbs">
          {i18n('globalSearch.result', { result: categoriesDisplayName })}
        </ProductSearchOptionSubTitle>
      )}
    </StyledProductSearchOption>
  );
}

function JobOption({ title, productId, highlightRegex, redirectURL }: Readonly<OptionProps>) {
  return (
    <StyledProductSearchOption data-testid={`link-to-${productId}`} to={redirectURL}>
      <ProductSearchOptionTitle>
        <Highlight text={title} regex={highlightRegex} />
      </ProductSearchOptionTitle>
    </StyledProductSearchOption>
  );
}

const StyledProductSearchOption = styled(Link)`
  background-color: inherit; // specified in the styled popper
  display: block;
  height: 100%;
  padding: 1rem;

  &:hover {
    background-color: ${({ theme }) =>
      alpha(
        theme.palette.common.white,
        0.08
      )} !important; // override all of the inherited colours from the listbox definition in the styled popper
  }
`;

const ProductSearchOptionTitle = styled(MUITypography)`
  font-size: 1.25rem;
  font-weight: bold;
`;

/* istanbul ignore next - need to fix the test */
const ProductSearchOptionSubTitle = styled(MUITypography)`
  font-size: 1rem;
  color: ${({ theme }) => theme.palette.common.white};
`;
